import React from "react";
import PropTypes from "prop-types";
import MediaQuery from "react-responsive";
import compose from "recompose/compose";
import setPropTypes from "recompose/setPropTypes";
import branch from "recompose/branch";
import branchServerClient from "web/core/branchServerClient";
import { useContext } from "react";

const SSR_VARIABLE_NAME = "__SSR_DEVICE__";

/**
 * @typedef {Object} Device
 * @property {string} type
 * @property {number} viewportWidthInPx
 */

/** @type {import('react').Context<Device>} */
const ResponsiveContext = React.createContext(null);
ResponsiveContext.displayName = "ResponsiveContext";

const defaultDevice = { type: "phone", viewportWidthInPx: 360 };

export const useDevice = () => {
  const result = useContext(ResponsiveContext);
  return result || defaultDevice;
};

export const ResponsiveContextProvider = ({ value, children }) => {
  const device = {
    viewportWidthInPx: value.device.viewportWidthInPx,
    type: value.device.type,
  };

  return (
    <React.Fragment>
      <script
        dangerouslySetInnerHTML={{
          __html: `window.${SSR_VARIABLE_NAME} = ${JSON.stringify(device)};`,
        }}
      />
      <ResponsiveContext.Provider value={device}>
        {children}
      </ResponsiveContext.Provider>
    </React.Fragment>
  );
};

export const getSSRDevice = () =>
  (typeof window !== "undefined" && window[SSR_VARIABLE_NAME]) || {
    type: "phone",
    viewportWidthInPx: 360,
  };

const matches = ({ minWidth, maxWidth }, width) =>
  (!minWidth || minWidth <= width) && (!maxWidth || width <= maxWidth);

const ClientMediaQuery = ({ minWidth, maxWidth, children, print }) => (
  <MediaQuery minWidth={minWidth} maxWidth={maxWidth} print={print}>
    {children}
  </MediaQuery>
);

// The server media query allows to make sure that the html that is used first
// is the same as the one rendered by the server.
// In react-responsive, this is not supported even though the `device` props allows
// to define what width should be used in the server. It is not used for the first
// render of the client.
const ServerMediaQuery = branch(
  (props) => typeof props.children === "function",
  () => (props) =>
    props.children(matches(props, props.device.viewportWidthInPx)),
  () => (props) =>
    matches(props, props.device.viewportWidthInPx) ? props.children : null
)(() => null);

export default compose(
  setPropTypes({
    minWidth: PropTypes.number,
    maxWidth: PropTypes.number,
    children: PropTypes.oneOfType([
      PropTypes.node.isRequired,
      PropTypes.func.isRequired,
    ]),
  }),
  (BaseComponent) => (props) =>
    (
      <ResponsiveContext.Consumer>
        {(device) => {
          if (!device) {
            if (process.env.NODE_ENV === "development") {
              throw new Error(
                `You should use <MediaQuery> inside a <ResponsiveContext.Provider>. Please check your top level component definition.`
              );
            }
            device = defaultDevice;
          }

          return <BaseComponent {...props} device={device} />;
        }}
      </ResponsiveContext.Consumer>
    ),
  branchServerClient(
    () => ServerMediaQuery,
    () => ClientMediaQuery,
    { preferClientSide: true }
  )
)(() => null);
